import React, { useCallback, useEffect, useState, useMemo } from 'react';
import _ from 'lodash';
import queryString from 'query-string';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import SimpleTable, { SortDirection, createDefaultActions } from 'components/Common/SimpleTable/SimpleTable';
import { useTranslation } from 'localization/useTranslation';
import ContentPage from 'components/ContentView/ContentPage/ContentPage';
import DeleteProvisionTemplate from './DeleteProvisionTemplate';
import T from 'localization/LocalizationKeys';
import useAlerts from 'utils/hooks/useAlerts';
import useAPI from 'services/useAPI';
import useApiCall from 'services/useApiCall';
import useProviderQuery from 'components/Admin/Providers/useProviderQuery';
import { modelIdWithoutTemplateId } from 'components/Admin/Model/utils/model';

const DefaultSortBy = {
  path: 'name',
  direction: SortDirection.DESC
};

const columns = [
  {
    label: T.common.name,
    path: 'displayName',
    searchItem: true,
    isSortable: true
  },
  {
    label: 'type',
    path: 'type',
    isSortable: true
  }
];


const AdminProvisionTemplatesList = ({ breadcrumbs }) => {
  const T = useTranslation();
  const api = useAPI();
  const history = useHistory();
  const location = useLocation();
  const params = useParams();

  const providerQuery = useProviderQuery(params.providerId);
  const provider = providerQuery.data;

  const tagFilter = useMemo(() => {
    if (params.modelId) {
      return { tagFilter: 'assetModel=' + modelIdWithoutTemplateId(params.modelId) };
    }
    if (provider?.type) {
      return { tagFilter: 'providerType=' + provider.type };
    }
  }, [params.modelId, provider?.type]);

  const [alerts, addSuccessAlert, addErrorAlert] = useAlerts();

  const [providerClientTemplates, setProviderClientTemplates] = useState();
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);

  const [getProviderTemplates, isLoadingProviderTemplates] = useApiCall({
    startAsLoading: true,
    apiCall: api.core.getProviderClientTemplates,
    onSuccess: useCallback((templates) => {
      setProviderClientTemplates(templates);
    }, []),
    onError: useCallback((error) => {
      console.error(error);
      setProviderClientTemplates([]);
      addErrorAlert(error);
    }, [addErrorAlert])
  });

  const onNewProviderTemplate = useCallback(() => {
    history.push(queryString.stringifyUrl({
      url: 'templates/new-template',
      query: tagFilter
    }));
  }, [history, tagFilter]);

  const onViewProviderTemplate = useCallback((template) => {
    history.push({
      pathname: `templates/${template.id}`,
      search: location.search
    });
  }, [history, location.search]);

  useEffect(() => {
    if (!providerQuery.isLoading) {
      getProviderTemplates(tagFilter);
    }
  }, [getProviderTemplates, tagFilter, providerQuery.isLoading]);


  const cancelDelete = useCallback(() => setConfirmDeleteItem(null), []);
  const onDelete = useCallback(provider => setConfirmDeleteItem(provider), []);
  const actions = useMemo(() => createDefaultActions({ onDelete }), [onDelete]);
  const deletedProvider = useCallback((provider) => {
    setProviderClientTemplates(oldProviders => {
      return _.reject(oldProviders, { id: provider.id });
    });
    setConfirmDeleteItem(null);
    addSuccessAlert(T.common.request.delete.success);
  }, [T.common.request.delete.success, addSuccessAlert]);

  return (
    <ContentPage breadcrumb={breadcrumbs} help={'admin/provision-templates/list'}>
      { alerts }
      <SimpleTable
        isLoading={isLoadingProviderTemplates}
        data={providerClientTemplates}
        onRowClick={onViewProviderTemplate}
        sortBy={DefaultSortBy}
        globalActions={[{ onClick: onNewProviderTemplate }]}
        actions={actions}
        columns={columns}
        emptyListText={'Press Add to add a template'}
      />
      <DeleteProvisionTemplate
        confirmDeleteItem={confirmDeleteItem}
        onDelete={deletedProvider}
        cancelDelete={cancelDelete}
      />
    </ContentPage>
  );
};

export default AdminProvisionTemplatesList;
